'use client'

import { useCallback, useId } from 'react'
import { cn } from '@/lib/utils'

interface MartiniLoaderProps {
  className?: string
  size?: number
}

// Keep in sync with `--ml-dur` (2.2s) on `.martini-loader-stage` in globals.css.
const ML_DURATION_MS = 2200

export function MartiniLoader({ className, size = 72 }: MartiniLoaderProps) {
  const reactId = useId()
  const clipOuterId = `ml-clip-outer-${reactId}`
  const clipInnerId = `ml-clip-inner-${reactId}`

  // Phase-lock both nods to a shared wall clock so a loader that mounts as
  // another unmounts — e.g. the boot screen handing off to the boot→canvas
  // crossfade overlay — continues the motion instead of snapping back to frame
  // 0, which reads as a jump. A negative `animation-delay` means "already N ms
  // into the loop"; deriving it from `performance.now()` makes every instance
  // show the same frame at the same instant, so handoffs are seamless.
  //
  // Applied via a ref callback (not render) so the clock read stays out of the
  // pure render pass and off the server: the callback runs on the client during
  // commit, before the first paint, so there is no visible snap and no
  // hydration mismatch.
  const applyNodPhase = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    el.style.animationDelay = `${-((globalThis.performance?.now() ?? 0) % ML_DURATION_MS)}ms`
  }, [])

  return (
    <div
      className={cn('martini-loader-stage', className)}
      style={{ width: size, height: size }}
      role="img"
      // eslint-disable-next-line i18next/no-literal-string -- brand
      aria-label="Martini"
    >
      <div className="martini-loader-mark">
        <div className="martini-loader-glass-wrap" ref={applyNodPhase}>
          <svg
            viewBox="0 0 202 188"
            className="martini-loader-glass"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <g clipPath={`url(#${clipOuterId})`}>
              <g clipPath={`url(#${clipInnerId})`}>
                <path
                  d="M16.6444 102.577L17.9104 102.808L127.878 122.889L157.521 154.941L135.205 175.579L143.362 184.399L196.812 134.968L188.655 126.148L166.341 146.784L136.696 114.73L124.728 4.01948L124.589 2.74806L16.6444 102.577ZM119.831 70.8182L123.976 110.615L84.6232 103.379L84.6225 103.378L71.4318 100.965L42.4206 95.1037L114.738 28.2241L119.831 70.8182Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="1.28627"
                />
                <path d="M9.19238 -0.332764L48.8547 -37.0129" stroke="currentColor" strokeWidth="10.2902" />
              </g>
            </g>
            <defs>
              <clipPath id={clipOuterId}>
                <rect width="202" height="188" fill="white" />
              </clipPath>
              <clipPath id={clipInnerId}>
                <rect
                  width="178.792"
                  height="178.792"
                  fill="white"
                  transform="matrix(-0.734169 0.678967 0.678967 0.734169 124.816 -20.9401)"
                />
              </clipPath>
            </defs>
          </svg>
        </div>
        <div className="martini-loader-olive" ref={applyNodPhase} aria-hidden="true" />
      </div>
    </div>
  )
}
